/* -----------------Globals--------------- */
import { useEffect, useMemo, useRef } from "react";

import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import type {
	NativeSyntheticEvent,
	TextInput as NativeTextInput,
	TextInputKeyPressEventData,
} from "react-native";
/* -----------------UI--------------- */
import { TextInput } from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "../../../useAppTheme";

/* -----------------Child components--------------- */
import Box from "../../Box";

type MessageInputBarProps = {
	placeholder: string;
	label: string;
	localSearchQuery: string;
	handleAbortSearch: () => void;
	handleOnChangeInput: (text: string) => void;
	handleClearInput?: () => void;
	handleSubmitQuery: (query: string) => void;
	disabled?: boolean;
	icon?: keyof typeof MaterialCommunityIcons.glyphMap;
	showSubmitButton?: boolean;
	showAdornments?: boolean;
};

export const MessageInputBar = (props: MessageInputBarProps) => {
	const theme = useAppTheme();
	const inputRef = useRef<NativeTextInput>(null);
	const {
		placeholder,
		label,
		localSearchQuery,
		handleOnChangeInput,
		handleSubmitQuery,
		handleAbortSearch,
		handleClearInput,
		disabled,
		icon = "magnify",
		showSubmitButton = false,
		showAdornments,
	} = props;

	const handleKeyDown = (
		e: NativeSyntheticEvent<TextInputKeyPressEventData>,
	) => {
		const { key: keyValue } = e.nativeEvent;

		if (keyValue === "Enter") {
			handleSubmitQuery(localSearchQuery);
		}
		if (keyValue === "Escape") {
			if (localSearchQuery.length > 0) {
				handleClearInput?.();
			} else {
				inputRef?.current?.blur();
			}
		}
	};

	useEffect(() => {
		if (!disabled && inputRef.current) {
			inputRef.current.focus();
		}
	}, [disabled]);

	const rightIcon = useMemo(() => {
		if (!(showSubmitButton && showAdornments)) {
			return undefined;
		}

		if (disabled) {
			return (
				<TextInput.Icon
					icon={(props) => (
						<MaterialCommunityIcons
							{...props}
							name="stop-circle-outline"
							color={theme.colors.primary}
							size={24}
						/>
					)}
					onPress={handleAbortSearch}
				/>
			);
		}

		if (localSearchQuery.length > 0) {
			return (
				<TextInput.Icon
					icon={(props) => (
						<MaterialCommunityIcons
							{...props}
							name="send-outline"
							color={theme.colors.primary}
							size={24}
						/>
					)}
					onPress={() => handleSubmitQuery(localSearchQuery)}
				/>
			);
		}

		return undefined;
	}, [
		showSubmitButton,
		localSearchQuery,
		disabled,
		handleSubmitQuery,
		theme.colors.primary,
		handleAbortSearch,
		showAdornments,
	]);

	return (
		<Box>
			<TextInput
				ref={inputRef}
				mode="outlined"
				label={label}
				placeholder={placeholder}
				value={localSearchQuery}
				onChangeText={handleOnChangeInput}
				onKeyPress={handleKeyDown}
				disabled={disabled}
				multiline
				numberOfLines={3}
				autoFocus
				left={
					<TextInput.Icon
						icon={() => (
							<MaterialCommunityIcons
								name={icon}
								size={24}
								color={theme.colors.onSurfaceVariant}
							/>
						)}
					/>
				}
				right={rightIcon}
				style={{
					backgroundColor: theme.colors.surface,
				}}
			/>
		</Box>
	);
};
